import React, { useCallback } from "react";
import { OrganizationServicesOffered } from "@common/types/organization";
import { servicesOfferedLabels } from "@components/entity/constants";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { FilterActionsFooter } from "@design-system/Filter/FilterActionsFooter/FilterActionsFooter";
import styles from "./ServicesFilterOverlay.module.scss";

interface ServicesFilterOverlayProps {
    selectedServices: OrganizationServicesOffered[];
    onChange: (services: OrganizationServicesOffered[]) => void;
    applyButtonOnClick?: () => void;
}

const availableServices = Object.values(OrganizationServicesOffered);

export const ServicesFilterOverlay: React.FC<ServicesFilterOverlayProps> = ({
    selectedServices,
    onChange,
    applyButtonOnClick,
}) => {
    const handleChange = useCallback(
        (service: OrganizationServicesOffered) => {
            if (selectedServices.includes(service)) {
                onChange(selectedServices.filter((s) => s !== service));
            } else {
                onChange([...selectedServices, service]);
            }
        },
        [selectedServices, onChange],
    );

    const handleSelectAll = useCallback(() => {
        if (selectedServices.length === availableServices.length) {
            onChange([]);
        } else {
            onChange(availableServices);
        }
    }, [selectedServices.length, onChange]);

    return (
        <div className={styles.servicesFilterOverlay}>
            <Checkbox
                className={styles.option}
                label="All"
                checked={selectedServices.length === availableServices.length}
                onCheckedChange={handleSelectAll}
                labelPlacement="start"
            />
            {availableServices.map((service) => (
                <Checkbox
                    className={styles.option}
                    key={service}
                    label={servicesOfferedLabels[service]}
                    checked={selectedServices.includes(service)}
                    onCheckedChange={() => handleChange(service)}
                    labelPlacement="start"
                />
            ))}
            {applyButtonOnClick && (
                <FilterActionsFooter
                    selectedCount={selectedServices.length}
                    deselectFunction={() => onChange([])}
                    applyFunction={applyButtonOnClick}
                />
            )}
        </div>
    );
};
